import * as RadixDialog from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { cn } from '@/lib/cn'
import { Button } from './Button'
import { IconButton } from './IconButton'

// Dialogs are centred with inset + auto margins, not translate(-50%): the pop-in/out keyframes animate
// `transform`, which would replace a translate and draw the dialog off-centre until the animation ends.
const centred = 'fixed inset-0 z-50 m-auto h-fit w-[calc(100vw-2rem)]'

const overlayClasses =
  'fixed inset-0 z-40 bg-black/25 backdrop-blur-[6px] data-[state=open]:motion-safe:animate-fade-in data-[state=closed]:motion-safe:animate-fade-out'

type ConfirmDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description: ReactNode
  confirmLabel: string
  tone?: 'danger' | 'primary'
  onConfirm: () => void | Promise<void>
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  tone = 'primary',
  onConfirm,
}: ConfirmDialogProps) {
  const [busy, setBusy] = useState(false)
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className={overlayClasses} />
        <RadixDialog.Content
          role="alertdialog"
          className={cn(
            centred,
            'max-w-md',
            'rounded-3xl bg-surface p-6 shadow-lift data-[state=open]:motion-safe:animate-pop-in data-[state=closed]:motion-safe:animate-pop-out',
          )}
        >
          <RadixDialog.Title className="text-base font-semibold text-ink">{title}</RadixDialog.Title>
          <RadixDialog.Description className="mt-2 text-sm leading-6 text-ink-muted">
            {description}
          </RadixDialog.Description>
          <div className="mt-6 flex justify-end gap-2">
            <RadixDialog.Close asChild>
              <Button variant="outline" size="sm">
                Cancel
              </Button>
            </RadixDialog.Close>
            <Button
              variant={tone === 'danger' ? 'danger' : 'primary'}
              size="sm"
              disabled={busy}
              onClick={async () => {
                setBusy(true)
                try {
                  await onConfirm()
                  onOpenChange(false)
                } finally {
                  setBusy(false)
                }
              }}
            >
              {confirmLabel}
            </Button>
          </div>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  )
}

type SheetProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  side: 'left' | 'right'
  title: string
  className?: string
  children: ReactNode
  showClose?: boolean
}

/** Slide-over panel (mobile sidebar drawer, narrow-screen sources panel). Title is screen-reader only. */
export function Sheet({ open, onOpenChange, side, title, className, children, showClose = true }: SheetProps) {
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className={overlayClasses} />
        <RadixDialog.Content
          aria-describedby={undefined}
          className={cn(
            'glass-panel fixed inset-y-0 z-50 flex flex-col shadow-lift outline-none',
            side === 'left'
              ? 'left-0 data-[state=open]:motion-safe:animate-sheet-in-left data-[state=closed]:motion-safe:animate-sheet-out-left'
              : 'right-0 data-[state=open]:motion-safe:animate-sheet-in-right data-[state=closed]:motion-safe:animate-sheet-out-right',
            className,
          )}
        >
          <RadixDialog.Title className="sr-only">{title}</RadixDialog.Title>
          {showClose && (
            <RadixDialog.Close asChild>
              <IconButton label="Close" className="absolute right-3 top-3 z-10" tooltip={false}>
                <X />
              </IconButton>
            </RadixDialog.Close>
          )}
          {children}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  )
}

type ModalProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: ReactNode
  className?: string
  children: ReactNode
}

/** Centred dialog for forms (add person, upload, change password). */
export function Modal({ open, onOpenChange, title, description, className, children }: ModalProps) {
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className={overlayClasses} />
        <RadixDialog.Content
          {...(description ? {} : { 'aria-describedby': undefined })}
          className={cn(
            centred,
            'flex max-h-[calc(100dvh-2rem)] max-w-lg flex-col',
            'rounded-3xl bg-surface shadow-lift outline-none data-[state=open]:motion-safe:animate-pop-in data-[state=closed]:motion-safe:animate-pop-out',
            className,
          )}
        >
          <div className="flex items-start gap-3 px-6 pb-2 pt-6">
            <div className="min-w-0 flex-1">
              <RadixDialog.Title className="text-[17px] font-semibold tracking-snug text-ink">
                {title}
              </RadixDialog.Title>
              {description && (
                <RadixDialog.Description className="mt-1 text-sm leading-6 text-ink-muted">
                  {description}
                </RadixDialog.Description>
              )}
            </div>
            <RadixDialog.Close asChild>
              <IconButton label="Close" tooltip={false} className="-mr-2 -mt-1">
                <X />
              </IconButton>
            </RadixDialog.Close>
          </div>
          <div className="min-h-0 overflow-y-auto px-6 pb-6 pt-3">{children}</div>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  )
}
